/**
 * The aggregation formulas, shared by the atlas and the editorial service.
 *
 * The client confirmed the weighted form on 24 Aug 2026:
 *
 *     category | dimension score = Σ(scoreᵢ × weightᵢ) / Σ weightᵢ
 *
 * where i runs only over the indicators (or categories) that have a known
 * value. Renormalising by the weights actually used is what makes the result
 * independent of how many indicators happen to be collected — which is exactly
 * the situation of the v1 delivery, where 20 of 52 indicators exist and one
 * whole dimension does not.
 *
 * Two consequences worth stating, because they are easy to trip over:
 *   - weights need not sum to anything in particular; the denominator handles it
 *   - a weight of 0 removes an indicator from the calculation entirely
 *
 * A missing value is never a zero. `null` drops out of both the numerator and
 * the denominator; only an actual assessment of 0 pulls a score down.
 */

/** One entry of the indicator catalogue. */
export interface Indicator {
  /** dimension */
  d: string;
  /** category */
  c: string;
  /** indicator name */
  n: string;
  /** indicator weight, default 1 */
  w?: number;
}

/** An assessment: 0–4, or null for "no data" / "not applicable". */
export type Cell = number | null;

export interface Aggregates {
  /** keyed "Dimension|Category" */
  cat: Record<string, number | null>;
  dim: Record<string, number | null>;
  overall: number | null;
}

export interface AggregateOptions {
  /** fixed dimension order; dimensions with nothing collected still appear, as null */
  dims: string[];
  /** category weights, keyed "Dimension|Category"; default 1 */
  categoryWeights?: Record<string, number>;
  /** dimension weights for the overall level; default 1 — still an open question with the client */
  dimensionWeights?: Record<string, number>;
}

/** Σ(value × weight) / Σ weight over the entries that have a value. */
export function weightedMean(entries: Iterable<readonly [Cell, number]>): number | null {
  let num = 0, den = 0;
  for (const [value, weight] of entries) {
    if (value == null || !(weight > 0)) continue;
    num += value * weight;
    den += weight;
  }
  return den > 0 ? num / den : null;
}

export const catKey = (dimension: string, category: string) => `${dimension}|${category}`;

/** Categories of each dimension, in first-seen order. */
export function categoriesByDimension(indicators: Indicator[], dims: string[]): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const d of dims) out[d] = [];
  for (const x of indicators) {
    (out[x.d] ??= []);
    if (!out[x.d].includes(x.c)) out[x.d].push(x.c);
  }
  return out;
}

/**
 * Roll one entity's indicator assessments up to categories, dimensions and an
 * overall capability level. `scores` is positionally aligned with `indicators`.
 */
export function aggregate(indicators: Indicator[], scores: readonly Cell[], opts: AggregateOptions): Aggregates {
  if (scores.length !== indicators.length) {
    throw new Error(`expected ${indicators.length} scores, got ${scores.length}`);
  }
  const byDim = categoriesByDimension(indicators, opts.dims);
  const cat: Record<string, number | null> = {};
  const dim: Record<string, number | null> = {};

  for (const d of opts.dims) {
    const categoryEntries: Array<readonly [Cell, number]> = [];
    for (const c of byDim[d]) {
      const key = catKey(d, c);
      cat[key] = weightedMean(
        indicators.flatMap((x, i) => (x.d === d && x.c === c ? [[scores[i], x.w ?? 1] as const] : [])),
      );
      categoryEntries.push([cat[key], opts.categoryWeights?.[key] ?? 1] as const);
    }
    dim[d] = weightedMean(categoryEntries);
  }

  const overall = weightedMean(opts.dims.map((d) => [dim[d], opts.dimensionWeights?.[d] ?? 1] as const));
  return { cat, dim, overall };
}

/* ---------------------------------------------------------------- levels */

/** Capability maturity levels. Bands over any 0–4 value — never a rank. */
export const LEVEL_NAMES = ['Emerging', 'Developing', 'Established', 'Advanced'] as const;
export type LevelName = (typeof LEVEL_NAMES)[number];

/** Level index for a value, or null when there is nothing to place. */
export function levelOf(v: number | null | undefined): 0 | 1 | 2 | 3 | null {
  if (v == null) return null;
  return v >= 3 ? 3 : v >= 2 ? 2 : v >= 1 ? 1 : 0;
}
